export class PlayerUIBinder {
	constructor(yt, options = {}) {
		this.yt = yt
		this.opts = options

		this.state = {
			loop: true,
			sectionLoop: false,
			abLoop: false,
			countMode: false,
			skip: false,
			flipped: true,
			seeking: false,
			isInitialized: false,
			hoveredLabel: null,
			longPressTimer: null,
			isLongPress: false,
			selectionStartTime: null
		}

		this.els = {
			thumb: document.querySelector('#ytThumb'),
			img: document.querySelector('#ytThumbImg'),
			playerWrap: document.querySelector('.c-youtube__playerWrap'),
			playerVisible: document.querySelector('#playerVisible'),
			playToggle: document.querySelector('#playToggle'),
			loopBtn: document.querySelector('#loopBtn'),
			flipBtn: document.querySelector('#flipBtn'),
			seekbar: document.querySelector('#seekbar'),
			currentTime: document.querySelector('#currentTime'),
			duration: document.querySelector('#duration'),
			markerLabel: document.querySelector('#markerLabel'),
			seekbarMarkers: document.querySelector('#seekbarMarkers'),
			seekbarWrap: document.querySelector('.c-youtube__seekbarWrap'),
			volume: document.querySelector('#volume'),
			muteBtn: document.querySelector('#muteBtn'),
			skipBtn: document.querySelector('#skipBtn'),
			abLoopBtn: document.querySelector('#abLoopBtn'),
			sectionLoopBtn: document.querySelector('#sectionLoopBtn'),
			countBtn: document.querySelector('#countBtn'),
			setPointA: document.querySelector('#setPointA'),
			setPointB: document.querySelector('#setPointB'),
			markerList: document.querySelector('#markerList')
		}

		this._bind()
	}

	_bind() {
		this._bindThumbnail()
		this._bindBasic()
		this._bindFrame()
		this._bindMarker()
		this._bindLoop()
		this._bindABLoop()
		this._bindSectionLoop()
		this._bindCountMode()
		this._bindSkip()
		this._bindSpeed()
		this._bindVolume()
		this._bindMute()
		this._bindSeek()
		this._bindFlip()
		this._bindKeyboard()
		this._bindMarkerHover()
		this._bindStateClass()
	}

	/* ======================
	   サムネイル
	====================== */

	_bindThumbnail() {
		const { thumb, img, playerWrap } = this.els

		if (img) img.src = this.yt.thumbnailUrl

		if (thumb) {
			thumb.addEventListener('click', (e) => {
				e.stopPropagation()
				this.yt.play()
				this._hideThumbnail()
			})
		}

		if (playerWrap) {
			playerWrap.addEventListener('click', () => {
				// サムネイルが表示されている間はクリックイベントをスルー（サムネイルのクリックイベントに任せる）
				if (thumb && !thumb.classList.contains('is-hidden')) return
				this.yt.togglePlay()
			})
		}
	}

	_hideThumbnail() {
		if (this.els.thumb) {
			this.els.thumb.classList.add('is-hidden')
		}
	}

	/* ======================
	   基本操作
	====================== */

	_bindBasic() {
		// 再生/一時停止ボタン
		this._on('#playToggle', () => {
			this.yt.togglePlay()
			this._hideThumbnail()
		})
	}

	/* ======================
	   フレーム
	====================== */

	_bindFrame() {
		this._on('#nextFrame', () => {
			this.yt.pause()
			this.yt.nextFrame()
			this._hideThumbnail()
		})

		this._on('#prevFrame', () => {
			this.yt.pause()
			this.yt.prevFrame()
			this._hideThumbnail()
		})
	}

	/* ======================
	   マーカー
	====================== */

	_bindMarker() {
		this._on('#nextMarker', () => {
			this.yt.nextMarker()
			this._hideThumbnail()
		})
		this._on('#prevMarker', () => {
			this.yt.prevMarker()
			this._hideThumbnail()
		})
	}

	/* ======================
	   ループ
	====================== */

	_bindLoop() {
		// 初期設定
		if (this.state.loop) {
			const { start, end } = this.opts.loop || { start: 10, end: 20 }
			this.yt.setLoop(start, end)
			this.yt.toggleLoop(true)
			this.els.loopBtn?.classList.add('is-active')
		}

		this._on('#loopBtn', () => {
			this.state.loop = !this.state.loop

			if (this.state.loop) {
				const { start, end } = this.opts.loop || { start: 10, end: 20 }

				this.yt.setLoop(start, end)
				this.yt.toggleLoop(true)
				if (typeof this.yt.preloadLoop === 'function') {
					this.yt.preloadLoop(start, end)
				}
				this._hideThumbnail()

				this.els.loopBtn?.classList.add('is-active')
			} else {
				this.yt.toggleLoop(false)
				this.els.loopBtn?.classList.remove('is-active')
			}
		})
	}

	/* ======================
	   ABループ
	====================== */

	_bindABLoop() {
		// ABループは非推奨だが一応残す（または削除）
		const { abLoopBtn, setPointA, setPointB } = this.els
		// ... 既存のロジック ...
	}

	/* ======================
	   区画ループ（ラベル間ループ）
	====================== */

	_bindSectionLoop() {
		const { sectionLoopBtn } = this.els

		if (sectionLoopBtn) {
			sectionLoopBtn.addEventListener('click', (e) => {
				e.stopPropagation()
				this.state.sectionLoop = !this.state.sectionLoop
				if (this.state.sectionLoop) {
					sectionLoopBtn.classList.add('is-active')
				} else {
					sectionLoopBtn.classList.remove('is-active')
					this.yt.resetSectionRange()
				}
				this.yt.toggleSectionLoop(this.state.sectionLoop)
			})
		}
	}

	/* ======================
	   スキップ
	====================== */

	_bindSkip() {
		// 初期設定
		this.yt.setSkips(this.opts.skips || [])
		this.yt.toggleSkips(this.state.skip)
		if (this.state.skip) {
			this.els.skipBtn?.classList.add('is-active')
			this.els.seekbarMarkers?.classList.add('is-skip-active')
		} else {
			this.els.skipBtn?.classList.remove('is-active')
			this.els.seekbarMarkers?.classList.remove('is-skip-active')
		}

		this._on('#skipBtn', () => {
			this.state.skip = !this.state.skip

			if (this.state.skip) {
				this.yt.toggleSkips(true)
				this.els.skipBtn?.classList.add('is-active')
				this.els.seekbarMarkers?.classList.add('is-skip-active')
			} else {
				this.yt.toggleSkips(false)
				this.els.skipBtn?.classList.remove('is-active')
				this.els.seekbarMarkers?.classList.remove('is-skip-active')
			}
		})
	}

	_bindCountMode() {
		this._on('#countBtn', (e) => {
			e.stopPropagation()
			this.state.countMode = !this.state.countMode
			this.yt.toggleCountMode(this.state.countMode)
			this._updateCountModeUI()
		})
		
		// 初期状態の反映
		this.yt.toggleCountMode(this.state.countMode)
		this._updateCountModeUI()
	}

	_updateCountModeUI() {
		const btn = this.els.countBtn
		if (!btn) return
		if (this.state.countMode) {
			btn.classList.add('is-active')
		} else {
			btn.classList.remove('is-active')
		}
	}

	/* ======================
	   再生速度
	====================== */

	_bindSpeed() {
		this._on('#speed', (e) => {
			this.yt.setSpeed(parseFloat(e.target.value))
			this._hideThumbnail()
		}, 'change')
	}

	/* ======================
	   ボリューム
	====================== */

	_bindVolume() {
		this._on('#volume', (e) => {
			this.yt.setVolume(parseInt(e.target.value, 10))
		}, 'input')
	}

	/* ======================
	   ミュート
	====================== */

	_bindMute() {
		this._on('#muteBtn', () => {
			if (this.yt.isMuted()) {
				this.yt.unMute()
			} else {
				this.yt.mute()
			}
			this._updateMuteUI()
		})
	}

	_updateMuteUI() {
		const btn = this.els.muteBtn
		if (!btn) return

		if (this.yt.isMuted()) {
			btn.classList.add('is-muted')
		} else {
			btn.classList.remove('is-muted')
		}
	}

	/* ======================
	   シークバー
	====================== */

	_bindSeek() {
		const { seekbar: seek, currentTime: currentTimeEl, duration: durationEl } = this.els

		if (!seek) {
			console.warn('seekbar not found')
			return
		}

		// ドラッグ開始
		seek.addEventListener('mousedown', () => {
			this.state.seeking = true
		})

		seek.addEventListener('mouseup', () => {
			this.state.seeking = false
		})

		// 値変更
		seek.addEventListener('input', (e) => {
			const duration = this.yt.getDuration()
			if (!duration || duration === Infinity) return

			// フレーム単位で計算 (value は現在何フレーム目かを表す)
			const targetFrame = parseInt(e.target.value, 10)
			const targetTime = targetFrame / this.yt.fps
			
			this.yt.seek(targetTime)
			this._hideThumbnail()

			// 表示の更新（リアルタイム性を高める）
			if (currentTimeEl) currentTimeEl.textContent = this._formatTime(targetTime, this.yt.fps)
			this._updateSeekbarBg(targetFrame, seek.max)
		})

		// 自動更新
		this._seekTimer = setInterval(() => {
			const duration = this.yt.getDuration()
			if (!duration || duration === Infinity) return

			this._initSeekbar(duration)

			if (durationEl) durationEl.textContent = this._formatTime(duration, this.yt.fps)

			const current = this.yt.getCurrentTime()
			if (currentTimeEl) currentTimeEl.textContent = this._formatTime(current, this.yt.fps)

			this._updateMarkerLabel(current)
			this._updateActiveSection(current)
			this._updateMuteUI()

			if (!this.state.seeking) {
				const currentFrame = Math.floor(current * this.yt.fps)
				seek.value = currentFrame
				this._updateSeekbarBg(currentFrame, seek.max)
			}
		}, 100)
	}

	_formatTime(seconds, fps) {
		const m = Math.floor(seconds / 60)
		const s = Math.floor(seconds % 60)
		const f = Math.floor((seconds % 1) * fps)
		return `${m}:${s.toString().padStart(2, '0')}.${f.toString().padStart(2, '0')}`
	}

	_initSeekbar(duration) {
		const seek = this.els.seekbar
		if (!seek || this.state.isInitialized || !duration || duration === Infinity) return
		const totalFrames = Math.floor(duration * this.yt.fps)
		seek.max = totalFrames
		seek.step = 1
		this.state.isInitialized = true
		this._renderMarkers(duration)
		this._renderMarkerList()
	}

	_renderMarkers(duration) {
		const container = this.els.seekbarMarkers
		if (!container || !duration) return

		container.innerHTML = ''
		this.yt.markers.forEach(marker => {
			const percent = (marker.time / duration) * 100
			const markerEl = document.createElement('div')
			markerEl.classList.add('c-youtube__marker')
			markerEl.dataset.time = marker.time
			if (marker.isSkip) {
				markerEl.classList.add('is-skip')
			}
			markerEl.style.left = `${percent}%`
			markerEl.dataset.label = marker.label
			
			// ラベル用の子要素を追加
			const labelInner = document.createElement('span')
			labelInner.classList.add('c-youtube__markerTooltip')
			labelInner.textContent = marker.label
			markerEl.appendChild(labelInner)
			
			markerEl.addEventListener('mouseenter', () => {
				this.state.hoveredLabel = marker.label
			})
			markerEl.addEventListener('mouseleave', () => {
				this.state.hoveredLabel = null
			})

			markerEl.addEventListener('click', (e) => {
				e.stopPropagation() // 親要素へのクリック伝播を止める
				if (this.state.isLongPress) return // 長押し時はクリックイベントを無視

				if (this.state.sectionLoop) {
					this._handleSectionClick(marker.time, e.shiftKey)
				} else {
					this.yt.seek(marker.time)
				}
				this.yt.play() // 再生を開始
				this._hideThumbnail()
			})

			// タッチイベントの追加
			markerEl.addEventListener('touchstart', (e) => {
				if (!this.state.sectionLoop) return
				this.state.isLongPress = false
				this.state.selectionStartTime = marker.time
				this.state.longPressTimer = setTimeout(() => {
					this.state.isLongPress = true
					markerEl.classList.add('is-selecting')
					// 選択モード開始時にバイブレーション（対応デバイスのみ）
					if (navigator.vibrate) navigator.vibrate(50)
				}, 500)
			}, { passive: true })

			markerEl.addEventListener('touchmove', (e) => {
				if (!this.state.isLongPress) return
				e.preventDefault()
				
				const touch = e.touches[0]
				const el = document.elementFromPoint(touch.clientX, touch.clientY)
				const targetMarker = el?.closest('.c-youtube__marker, .c-youtube__markerListItem')
				
				if (targetMarker && targetMarker.dataset.time) {
					const currentTime = parseFloat(targetMarker.dataset.time)
					this._highlightRange(this.state.selectionStartTime, currentTime)
				}
			}, { passive: false })

			markerEl.addEventListener('touchend', (e) => {
				clearTimeout(this.state.longPressTimer)
				if (this.state.isLongPress) {
					const touch = e.changedTouches[0]
					const el = document.elementFromPoint(touch.clientX, touch.clientY)
					const targetMarker = el?.closest('.c-youtube__marker, .c-youtube__markerListItem')
					
					if (targetMarker && targetMarker.dataset.time) {
						const endTime = parseFloat(targetMarker.dataset.time)
						this._handleSectionClickRange(this.state.selectionStartTime, endTime)
					}
					
					this.state.isLongPress = false
					document.querySelectorAll('.is-selecting').forEach(el => el.classList.remove('is-selecting'))
				}
			})

			container.appendChild(markerEl)
		})
	}

	_renderMarkerList() {
		const container = this.els.markerList
		if (!container) return

		container.innerHTML = ''
		const filteredMarkers = this.yt.markers.filter(m => !m.isSkip && !m.label.includes('SKIP'))

		filteredMarkers.forEach(marker => {
			const item = document.createElement('div')
			item.classList.add('c-youtube__markerListItem')
			item.dataset.time = marker.time

			const timeStr = this._formatTime(marker.time, this.yt.fps)
			item.innerHTML = `<span class="c-youtube__markerListTime">${timeStr}</span><span class="c-youtube__markerListLabel">${marker.label}</span>`

			item.addEventListener('click', (e) => {
				if (this.state.isLongPress) return // 長押し時はクリックイベントを無視

				if (this.state.sectionLoop) {
					this._handleSectionClick(marker.time, e.shiftKey)
				} else {
					// 通常クリック
					this.yt.seek(marker.time)
				}
				this.yt.play() // 再生を開始
				this._hideThumbnail()
			})

			// タッチイベントの追加
			item.addEventListener('touchstart', (e) => {
				if (!this.state.sectionLoop) return
				this.state.isLongPress = false
				this.state.selectionStartTime = marker.time
				this.state.longPressTimer = setTimeout(() => {
					this.state.isLongPress = true
					item.classList.add('is-selecting')
					// 選択モード開始時にバイブレーション（対応デバイスのみ）
					if (navigator.vibrate) navigator.vibrate(50)
				}, 500)
			}, { passive: true })

			item.addEventListener('touchmove', (e) => {
				if (!this.state.isLongPress) return
				e.preventDefault()
				
				const touch = e.touches[0]
				const el = document.elementFromPoint(touch.clientX, touch.clientY)
				const targetMarker = el?.closest('.c-youtube__marker, .c-youtube__markerListItem')
				
				if (targetMarker && targetMarker.dataset.time) {
					const currentTime = parseFloat(targetMarker.dataset.time)
					this._highlightRange(this.state.selectionStartTime, currentTime)
				}
			}, { passive: false })

			item.addEventListener('touchend', (e) => {
				clearTimeout(this.state.longPressTimer)
				if (this.state.isLongPress) {
					const touch = e.changedTouches[0]
					const el = document.elementFromPoint(touch.clientX, touch.clientY)
					const targetMarker = el?.closest('.c-youtube__marker, .c-youtube__markerListItem')
					
					if (targetMarker && targetMarker.dataset.time) {
						const endTime = parseFloat(targetMarker.dataset.time)
						this._handleSectionClickRange(this.state.selectionStartTime, endTime)
					}
					
					this.state.isLongPress = false
					document.querySelectorAll('.is-selecting').forEach(el => el.classList.remove('is-selecting'))
				}
			})

			container.appendChild(item)
		})
	}

	_bindMarkerHover() {
		// マーカー要素へのイベント委譲は _renderMarkers で行うため、ここでは何もしないか
		// あるいは mousemove による判定を削除または補助的に使用する
	}

	_updateSeekbarBg(val, max) {
		const seek = this.els.seekbar
		if (!seek) return
		const percent = (val / max) * 100
		
		// ブラウザの range input はつまみの中心が (半径) から (全体-半径) まで動く
		// つまみの幅が 12px なので半径は 6px
		const thumbRadius = 6
		const width = seek.offsetWidth || 0
		
		let adjustedPercent = percent
		if (width > 0) {
			// 0% のときに 6px 分の割合、100% のときに (width-6px) 分の割合になるように補正
			const startPercent = (thumbRadius / width) * 100
			const endPercent = ((width - thumbRadius) / width) * 100
			adjustedPercent = startPercent + (percent * (endPercent - startPercent) / 100)
		}
		
		seek.style.background = `linear-gradient(to right, #ff0000 0%, #ff0000 ${adjustedPercent}%, #eee ${adjustedPercent}%, #eee 100%)`
	}

	_updateMarkerLabel(time) {
		const labelEl = this.els.markerLabel
		if (!labelEl) return

		const currentMarker = this.yt.getCurrentMarker()
		if (currentMarker) {
			labelEl.textContent = currentMarker.label
		} else {
			labelEl.textContent = ''
		}
	}

	_updateActiveSection(time) {
		const { seekbarMarkers, markerList } = this.els
		if (!this.state.sectionLoop) {
			// 区画ループが無効な場合はクラスをすべて削除して終了
			seekbarMarkers?.querySelectorAll('.is-active-section').forEach(el => {
				el.classList.remove('is-active-section')
			})
			markerList?.querySelectorAll('.is-active-section').forEach(el => {
				el.classList.remove('is-active-section')
			})
			return
		}

		const start = this.yt.sectionLoop.start
		const end = this.yt.sectionLoop.end

		// 範囲内のマーカーとリスト項目をハイライトする
		if (seekbarMarkers) {
			seekbarMarkers.querySelectorAll('.c-youtube__marker').forEach(markerEl => {
				const t = parseFloat(markerEl.dataset.time)
				
				if (t >= start && t < end) {
					markerEl.classList.add('is-active-section')
				} else {
					markerEl.classList.remove('is-active-section')
				}
			})
		}

		if (markerList) {
			markerList.querySelectorAll('.c-youtube__markerListItem').forEach(itemEl => {
				const t = parseFloat(itemEl.dataset.time)

				if (t >= start && t < end) {
					itemEl.classList.add('is-active-section')
				} else {
					itemEl.classList.remove('is-active-section')
				}
			})
		}
	}

	_handleSectionClick(time, isShift = false) {
		if (isShift) {
			this._handleSectionClickRange(this.yt.sectionLoop.start, time)
		} else {
			// 通常クリック：即座にその地点の単一区画ループへと切り替える
			this.yt.resetSectionRange()
			this.yt.seek(time)
			this.yt.play() // 再生を開始
		}
	}

	_handleSectionClickRange(startTime, endTime) {
		const times = [startTime, endTime].sort((a, b) => a - b)
		const sorted = [...this.yt.markers].sort((a, b) => a.time - b.time)
		const endIdx = sorted.findIndex(m => m.time === times[1])
		
		let endPos = 0
		if (endIdx !== -1 && endIdx + 1 < sorted.length) {
			endPos = sorted[endIdx + 1].time
		} else {
			endPos = this.yt.getDuration()
		}
		
		this.yt.setSectionRange(times[0], endPos)
		this.yt.seek(times[0])
		this.yt.play() // 再生を開始
	}

	_highlightRange(startTime, endTime) {
		const times = [startTime, endTime].sort((a, b) => a - b)
		const markers = this.els.seekbarMarkers?.querySelectorAll('.c-youtube__marker')
		const listItems = this.els.markerList?.querySelectorAll('.c-youtube__markerListItem')

		const highlight = (els) => {
			els?.forEach(el => {
				const t = parseFloat(el.dataset.time)
				if (t >= times[0] && t <= times[1]) {
					el.classList.add('is-selecting')
				} else {
					el.classList.remove('is-selecting')
				}
			})
		}

		highlight(markers)
		highlight(listItems)
	}

	/* ======================
	   反転
	====================== */

	_bindFlip() {
		// 初期設定
		const targetEl = document.getElementById(this.yt.visibleId)
		const thumbEl = this.els.thumb

		const applyFlip = (flipped) => {
			if (flipped) {
				if (targetEl) this.yt.flip(targetEl)
				if (thumbEl) this.yt.flip(thumbEl)
				this.els.flipBtn?.classList.add('is-active')
			} else {
				if (targetEl) this.yt.resetFlip(targetEl)
				if (thumbEl) this.yt.resetFlip(thumbEl)
				this.els.flipBtn?.classList.remove('is-active')
			}
		}

		applyFlip(this.state.flipped)

		this._on('#flipBtn', () => {
			this.state.flipped = !this.state.flipped
			applyFlip(this.state.flipped)
		})
	}

	/* ======================
	   キーボード
	====================== */

	_bindKeyboard() {
		document.addEventListener('keydown', (e) => {
			// 入力要素にフォーカスがある場合は無視
			const activeEl = document.activeElement
			if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'SELECT' || activeEl.tagName === 'TEXTAREA')) {
				return
			}

			switch (e.key) {
				case ' ':
					e.preventDefault()
					this.yt.togglePlay()
					this._hideThumbnail()
					break

				case 'ArrowRight':
					this.yt.nextFrame()
					this._hideThumbnail()
					break

				case 'ArrowLeft':
					this.yt.prevFrame()
					this._hideThumbnail()
					break

				case 'm':
					this.yt.nextMarker()
					this._hideThumbnail()
					break
			}
		})
	}

	/* ======================
	   状態クラスの管理
	====================== */

	_bindStateClass() {
		const { playerWrap, playToggle } = this.els

		this.yt.onStateChange = (state) => {
			const PLAYING = 1
			const PAUSED = 2
			
			if (state === PLAYING) {
				this._hideThumbnail()
				playerWrap?.classList.add('is-playing')
				playerWrap?.classList.remove('is-paused')
				playToggle?.classList.add('is-playing')
				playToggle?.classList.remove('is-paused')
			} else if (state === PAUSED) {
				playerWrap?.classList.add('is-paused')
				playerWrap?.classList.remove('is-playing')
				playToggle?.classList.add('is-paused')
				playToggle?.classList.remove('is-playing')
			} else {
				// それ以外の状態（終了時など）
				playerWrap?.classList.remove('is-playing')
				playerWrap?.classList.remove('is-paused')
				playToggle?.classList.remove('is-playing')
				playToggle?.classList.remove('is-paused')
			}
		}
	}

	/* ======================
	   ユーティリティ
	====================== */

	_on(selector, handler, event = 'click') {
		const el = document.querySelector(selector)

		if (!el) {
			console.warn('Element not found:', selector)
			return
		}

		el.addEventListener(event, handler)
	}

	destroy() {
		clearInterval(this._seekTimer)
	}
}